/**
 * TestDriveModal — Jun-2026 (User-Wunsch).
 *
 * „Probefahrt durchführen" in der Disposition-Fahrzeugliste. Erfasst Kunden- und
 * Fahrzeugdaten (Fahrzeug vorbefüllt aus dem Inserat), Überlassungszeitraum,
 * Versicherungs-/Kosten-Konditionen und sonstige Vereinbarungen. Erzeugt die
 * neutrale Probefahrt-Vereinbarung als PDF und legt automatisch einen Lead an.
 */
import { useEffect, useState } from "react";
import { Car, Loader2, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { previewPdfResponse, customerRecipient } from "../../lib/pdfPreview";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { VinWarning } from "../common/VinWarning";

const fmtDateTime = (v) => {
    if (!v) return "";
    // "2026-06-10T10:00" → "10.06.2026 10:00"
    const [d, t] = v.split("T");
    if (!d) return v;
    const [y, m, day] = d.split("-");
    return `${day}.${m}.${y}${t ? " " + t : ""}`;
};

const Field = ({ label, children, testid, required }) => (
    <div>
        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
            {label}{required && <span className="text-red-500"> *</span>}
        </Label>
        <div className="mt-1" data-testid={testid}>{children}</div>
    </div>
);

export default function TestDriveModal({ item, onClose }) {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [customer, setCustomer] = useState({
        first_name: "", last_name: "", street: "", postal_code: "", city: "",
        phone: "", email: "", id_card_number: "", license_number: "",
        license_issue_date: "", license_authority: "",
    });
    const [vehicle, setVehicle] = useState({
        manufacturer: "", model: "", displacement: "", max_speed_kmh: "",
        first_registration_date: "", max_weight_kg: "", vin: "", start_km: "",
        is_registered: false, license_plate: "", red_plate: "",
    });
    const [period, setPeriod] = useState({ from: "", to: "" });
    const [insurance, setInsurance] = useState({ teilkasko_self: 1000, vollkasko_self: 1000 });
    const [costs, setCosts] = useState({ free_km: 100, extra_km_price: 0.15 });
    const [notes, setNotes] = useState("");
    const [sendEmail, setSendEmail] = useState(false);
    const [consentMarketing, setConsentMarketing] = useState(false);
    const [redPlates, setRedPlates] = useState([]);

    useEffect(() => {
        let on = true;
        (async () => {
            setLoading(true);
            try {
                const [{ data: l }, rp] = await Promise.all([
                    api.get(`/listings/${item.id}`),
                    api.get("/dealer/red-plates").catch(() => ({ data: { items: [] } })),
                ]);
                if (!on) return;
                const plates = (rp.data?.items || []).filter((p) => p.active);
                setRedPlates(rp.data?.items || []);
                const registered = !!l.license_plate;
                setVehicle({
                    manufacturer: l.manufacturer || l.brand || "",
                    model: l.model || "",
                    displacement: l.displacement ?? "",
                    max_speed_kmh: l.max_speed_kmh ?? "",
                    first_registration_date: l.first_registration_date || "",
                    max_weight_kg: l.max_weight_kg ?? "",
                    vin: l.vin || "",
                    start_km: l.mileage ?? "",
                    is_registered: registered,
                    license_plate: l.license_plate || "",
                    // Jun-2026 — bei nicht zugelassenem Fahrzeug das erste aktive
                    // Probefahrtkennzeichen (Rote Nummer) automatisch übernehmen.
                    red_plate: registered ? "" : (plates[0]?.plate || ""),
                });
            } catch {
                toast.error("Fahrzeugdaten konnten nicht geladen werden.");
            } finally {
                if (on) setLoading(false);
            }
        })();
        return () => { on = false; };
    }, [item.id]);

    const cu = (k, v) => setCustomer((p) => ({ ...p, [k]: v }));
    const vu = (k, v) => setVehicle((p) => ({ ...p, [k]: v }));

    const validate = () => {
        const isEmpty = (v) => v === null || v === undefined || String(v).trim() === "";
        const missing = [];
        const reqCustomer = [
            ["first_name", "Vorname"], ["last_name", "Nachname"], ["phone", "Handynummer"],
            ["street", "Straße & Nr."], ["postal_code", "PLZ"], ["city", "Ort"],
            ["email", "E-Mail"], ["id_card_number", "Personalausweis-Nr."], ["license_number", "Führerschein-Nr."],
        ];
        reqCustomer.forEach(([k, l]) => { if (isEmpty(customer[k])) missing.push(l); });
        // Höchstgeschw., zul. Gesamtgewicht, FIN & Kennzeichen sind OPTIONAL
        // (importierte/König-Fahrzeuge liefern diese oft nicht) — bleiben im PDF leer.
        const reqVehicle = [
            ["manufacturer", "Hersteller"], ["model", "Modell"],
            ["displacement", "Hubraum"], ["start_km", "Anfangs-km-Stand"],
        ];
        reqVehicle.forEach(([k, l]) => { if (isEmpty(vehicle[k])) missing.push(l); });
        if (isEmpty(period.from)) missing.push("Probefahrt von");
        if (isEmpty(period.to)) missing.push("Probefahrt bis");
        [["teilkasko_self", "SB Teilkasko"], ["vollkasko_self", "SB Vollkasko"]].forEach(([k, l]) => { if (isEmpty(insurance[k])) missing.push(l); });
        [["free_km", "Freikilometer"], ["extra_km_price", "€ je Mehrkilometer"]].forEach(([k, l]) => { if (isEmpty(costs[k])) missing.push(l); });
        return missing;
    };

    const submit = async (mode = "download") => {
        const missing = validate();
        if (missing.length) {
            toast.error(`Bitte alle Pflichtfelder ausfüllen: ${missing.slice(0, 6).join(", ")}${missing.length > 6 ? " …" : ""}`);
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                customer: {
                    ...customer,
                    license_issue_date: customer.license_issue_date
                        ? new Date(customer.license_issue_date).toLocaleDateString("de-DE")
                        : "",
                },
                vehicle: { ...vehicle },
                period: { from: fmtDateTime(period.from), to: fmtDateTime(period.to) },
                insurance,
                costs,
                notes,
                consent_marketing: consentMarketing,
                send_email: sendEmail && !!customer.email.trim(),
            };
            const resp = await api.post(`/listings/${item.id}/probefahrt-pdf`, body, { responseType: "blob" });
            previewPdfResponse(resp, `Probefahrt-Vereinbarung-${customer.last_name || "Benutzer"}.pdf`, { recipient: customerRecipient(customer) });
            toast.success("Probefahrt-Vereinbarung erstellt & als Lead übernommen.");
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vereinbarung konnte nicht erstellt werden.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="testdrive-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Car className="w-5 h-5" /> Probefahrt durchführen
                    </DialogTitle>
                    <DialogDescription>
                        {item.brand} {item.model} — die Vereinbarung wird als PDF erstellt und automatisch als Lead zur Nachverfolgung übernommen.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-12 text-gray-500" data-testid="testdrive-loading">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Fahrzeugdaten werden geladen…
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* BENUTZER */}
                        <section>
                            <h3 className="text-xs font-black uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm">Benutzer (Kunde)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                                <Field label="Vorname" required testid="td-first-name"><Input value={customer.first_name} onChange={(e) => cu("first_name", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Nachname" required testid="td-last-name"><Input value={customer.last_name} onChange={(e) => cu("last_name", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Handynummer" required testid="td-phone"><Input value={customer.phone} onChange={(e) => cu("phone", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Straße & Nr." required testid="td-street"><Input value={customer.street} onChange={(e) => cu("street", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="PLZ" required testid="td-plz"><Input value={customer.postal_code} onChange={(e) => cu("postal_code", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Ort" required testid="td-city"><Input value={customer.city} onChange={(e) => cu("city", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="E-Mail" required testid="td-email"><Input type="email" value={customer.email} onChange={(e) => cu("email", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Personalausweis-Nr." required testid="td-id-card"><Input value={customer.id_card_number} onChange={(e) => cu("id_card_number", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Führerschein-Nr." required testid="td-license-nr"><Input value={customer.license_number} onChange={(e) => cu("license_number", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="FS-Ausstellungsdatum" testid="td-license-issue-date"><Input type="date" value={customer.license_issue_date} onChange={(e) => cu("license_issue_date", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Ausstellende Behörde" testid="td-license-authority"><Input value={customer.license_authority} onChange={(e) => cu("license_authority", e.target.value)} placeholder="z. B. LABO Berlin" className="rounded-sm" /></Field>
                            </div>
                        </section>

                        {/* FAHRZEUG */}
                        <section>
                            <h3 className="text-xs font-black uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm">Probefahrt-Fahrzeug</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                                <Field label="Hersteller" required testid="td-manufacturer"><Input value={vehicle.manufacturer} onChange={(e) => vu("manufacturer", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Modell" required testid="td-model"><Input value={vehicle.model} onChange={(e) => vu("model", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Hubraum (ccm)" required testid="td-displacement"><Input type="number" value={vehicle.displacement} onChange={(e) => vu("displacement", e.target.value)} className="rounded-sm font-mono-num" /></Field>
                                <Field label="Höchstgeschw. (km/h)" testid="td-max-speed"><Input type="number" value={vehicle.max_speed_kmh} onChange={(e) => vu("max_speed_kmh", e.target.value)} className="rounded-sm font-mono-num" /></Field>
                                <Field label="Erstzulassung" testid="td-erstzulassung"><Input type="date" value={vehicle.first_registration_date} onChange={(e) => vu("first_registration_date", e.target.value)} className="rounded-sm" /></Field>
                                <Field label="Zul. Gesamtgewicht (kg)" testid="td-max-weight"><Input type="number" value={vehicle.max_weight_kg} onChange={(e) => vu("max_weight_kg", e.target.value)} className="rounded-sm font-mono-num" /></Field>
                                <Field label="Fahrgestellnummer" testid="td-vin"><Input value={vehicle.vin} onChange={(e) => vu("vin", e.target.value)} className="rounded-sm" /><VinWarning value={vehicle.vin} /></Field>
                                <Field label="Anfangs-km-Stand" required testid="td-start-km"><Input type="number" value={vehicle.start_km} onChange={(e) => vu("start_km", e.target.value)} className="rounded-sm font-mono-num" /></Field>
                            </div>
                            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="td-is-registered">
                                <Checkbox checked={vehicle.is_registered} onCheckedChange={(c) => vu("is_registered", !!c)} />
                                Fahrzeug ist zugelassen (z.&nbsp;B. Vorführfahrzeug)
                            </label>
                            <div className="mt-2 max-w-xs">
                                {vehicle.is_registered ? (
                                    <Field label="Amtl. Kennzeichen" testid="td-license-plate"><Input value={vehicle.license_plate} onChange={(e) => vu("license_plate", e.target.value)} className="rounded-sm font-mono-num uppercase" /></Field>
                                ) : (
                                    <Field label="Rotes Kennzeichen (Probefahrtkennzeichen)" testid="td-red-plate">
                                        {redPlates.filter((p) => p.active).length > 0 ? (
                                            <select
                                                value={vehicle.red_plate}
                                                onChange={(e) => vu("red_plate", e.target.value)}
                                                className="w-full border border-gray-300 rounded-sm px-2 py-2 text-sm font-mono-num uppercase"
                                                data-testid="td-red-plate-select"
                                            >
                                                <option value="">— wählen —</option>
                                                {redPlates.filter((p) => p.active).map((p) => (
                                                    <option key={p.id} value={p.plate}>{p.plate}{p.note ? ` (${p.note})` : ""}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <Input value={vehicle.red_plate} onChange={(e) => vu("red_plate", e.target.value)} placeholder="06-XXXXX" className="rounded-sm font-mono-num uppercase" />
                                        )}
                                    </Field>
                                )}
                            </div>
                        </section>

                        {/* ZEITRAUM + KONDITIONEN */}
                        <section>
                            <h3 className="text-xs font-black uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm">Zeitraum & Konditionen</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                                <Field label="Probefahrt von" required testid="td-from"><Input type="datetime-local" value={period.from} onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))} className="rounded-sm" /></Field>
                                <Field label="Probefahrt bis" required testid="td-to"><Input type="datetime-local" value={period.to} min={period.from || undefined} onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))} className="rounded-sm" /></Field>
                                <div />
                                <Field label="SB Teilkasko (€)" required testid="td-tk"><Input type="number" value={insurance.teilkasko_self} onChange={(e) => setInsurance((p) => ({ ...p, teilkasko_self: e.target.value }))} className="rounded-sm font-mono-num" /></Field>
                                <Field label="SB Vollkasko (€)" required testid="td-vk"><Input type="number" value={insurance.vollkasko_self} onChange={(e) => setInsurance((p) => ({ ...p, vollkasko_self: e.target.value }))} className="rounded-sm font-mono-num" /></Field>
                                <div />
                                <Field label="Freikilometer" required testid="td-free-km"><Input type="number" value={costs.free_km} onChange={(e) => setCosts((p) => ({ ...p, free_km: e.target.value }))} className="rounded-sm font-mono-num" /></Field>
                                <Field label="€ je Mehrkilometer" required testid="td-extra-km"><Input type="number" step="0.01" value={costs.extra_km_price} onChange={(e) => setCosts((p) => ({ ...p, extra_km_price: e.target.value }))} className="rounded-sm font-mono-num" /></Field>
                            </div>
                            <div className="mt-3">
                                <Field label="Sonstige Vereinbarungen" testid="td-notes">
                                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm" placeholder="z. B. nur im Stadtgebiet, Begleitperson …" />
                                </Field>
                            </div>
                        </section>

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t">
                            <div className="flex flex-col gap-1.5">
                                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="td-consent-marketing">
                                    <Checkbox checked={consentMarketing} onCheckedChange={(c) => setConsentMarketing(!!c)} />
                                    Kunde willigt in Datenverwendung zu Werbezwecken ein (Ankreuzfeld im PDF)
                                </label>
                                <label className={`flex items-center gap-2 text-sm cursor-pointer ${customer.email.trim() ? "" : "opacity-50"}`} data-testid="td-send-email">
                                    <Checkbox checked={sendEmail} disabled={!customer.email.trim()} onCheckedChange={(c) => setSendEmail(!!c)} />
                                    Vereinbarung zusätzlich per E-Mail an den Kunden senden
                                </label>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="td-cancel">Abbrechen</Button>
                                <Button variant="outline" onClick={() => submit("print")} disabled={submitting} className="rounded-sm" data-testid="td-print">
                                    <Printer className="w-4 h-4 mr-2" /> Erstellen & Drucken
                                </Button>
                                <Button onClick={() => submit("download")} disabled={submitting} className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="td-submit">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                                    Vereinbarung erstellen (PDF)
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
